/**
 * Flow — slices 68–70. The graph a person draws, the settings that say when
 * people enter it, and every request and response the editor, the engine and
 * Sense exchange about it.
 *
 * ## THE GRAPH IS THE CONCEPT'S MODEL, TYPED
 *
 * Node types and ports are exactly `wend-concept/.../flow/model.js`'s: a single
 * `trigger` (port `out`); `message` — WhatsApp with quick-reply buttons has one
 * port per button (`btn0…btnN`) plus `noreply`, WhatsApp without buttons and
 * email have `out`; `wait` (`out`); `condition` (`yes` / `no`); `split` (one
 * port per row, plus `holdout` when a holdout is reserved); `update` (`out`);
 * and three terminals with no ports — `goal`, `exit`, `comment`. An edge is
 * `{ id, from, port, to }` and a port carries at most one edge.
 *
 * The one deliberate departure: the concept kept the split's holdout as a row
 * flagged `holdout: true`. Here it is `split.holdout: { pct } | null` and the
 * port is literally `holdout`, so "the holdout path" is a name the engine and
 * the stats can match on rather than a flag they must remember to look for —
 * and a holdout port can never be wired to a message by a row rename.
 *
 * A WhatsApp message node carries the quick-reply labels of its template AS
 * SAVED (`data.buttons`), so {@link portsOf} and {@link validateGraph} work on
 * a graph alone — the API refreshes that snapshot from the approved template on
 * every draft save and refuses a launch when they differ. A template whose
 * buttons changed shows up as edges on ports that no longer exist.
 *
 * ## ONE SET OF STRUCTURAL RULES, IN ONE PLACE
 *
 * {@link validateGraph} is pure and is called by the API on every draft save
 * (422 with the issues) and by the editor on every change (inline). One trigger;
 * every edge between nodes that exist, from a port its node has; at most one
 * edge per port; nothing into the trigger; nothing to or from a comment; no
 * cycle — graphs are DAGs, which is what lets `flow_steps` be unique per
 * (enrolment, node). What it deliberately does NOT refuse is an unfinished
 * graph: a message with no template yet, a split that adds to 90%, a path that
 * ends early. A draft is allowed to be a draft. Those are the preflight's
 * findings, and the bad ones block LAUNCH, not saving.
 *
 * ## SUGGESTIONS ARE DRAFTS
 *
 * A node with `suggested: { reason }` came from `POST /flows/:id/suggest` — a
 * deterministic rule, no AI call — and the editor draws it dashed with accept /
 * reject. Nothing suggested ever launches: preflight lists each one as a
 * blocking check until a person accepts (clears `suggested`) or removes it.
 *
 * ## IMPORTS
 *
 * `./rules.js` (a leaf), `./whatsapp.js` for the one binding shape every
 * WhatsApp send uses, `./segment.js` for the masked person. None of them imports
 * this file; `./today.js` does (for the template ids a Sense finding drafts),
 * and this file never imports it back.
 */
import { z } from 'zod';

import { emailSchema, isoDateTimeSchema, paginated, paginationQuerySchema, uuidSchema } from '../common.js';
import { ruleIdSchema, ruleJoinSchema, ruleLeafSchema, ruleDateSchema } from './rules.js';
import { maskedPersonSchema } from './segment.js';
import { waBindingListSchema } from './whatsapp.js';

/* -------------------------------------------------------------------------- */
/* Vocabularies                                                                */
/* -------------------------------------------------------------------------- */

export const FLOW_STATUS_VALUES = ['draft', 'live', 'paused', 'archived'] as const;
export const flowStatusSchema = z.enum(FLOW_STATUS_VALUES);
export type FlowStatus = z.infer<typeof flowStatusSchema>;

export const FLOW_STATUS_LABEL: Readonly<Record<FlowStatus, string>> = {
  draft: 'Draft',
  live: 'Live',
  paused: 'Paused',
  archived: 'Archived',
};

export const FLOW_VERSION_STATUS_VALUES = ['draft', 'live', 'retired'] as const;
export const flowVersionStatusSchema = z.enum(FLOW_VERSION_STATUS_VALUES);
export type FlowVersionStatus = z.infer<typeof flowVersionStatusSchema>;

export const FLOW_ENROLLMENT_STATUS_VALUES = ['active', 'waiting', 'completed', 'converted', 'exited', 'failed'] as const;
export const flowEnrollmentStatusSchema = z.enum(FLOW_ENROLLMENT_STATUS_VALUES);
export type FlowEnrollmentStatus = z.infer<typeof flowEnrollmentStatusSchema>;

export const FLOW_ENROLLMENT_STATUS_LABEL: Readonly<Record<FlowEnrollmentStatus, string>> = {
  active: 'In progress',
  waiting: 'Waiting',
  completed: 'Finished',
  converted: 'Converted',
  exited: 'Left early',
  failed: 'Failed',
};

/** Why an enrolment is `waiting`. Mirrors the CHECK in 0052. */
export const FLOW_WAIT_KIND_VALUES = ['wait', 'reply', 'quiet_hours', 'frequency_cap', 'rest'] as const;
export const flowWaitKindSchema = z.enum(FLOW_WAIT_KIND_VALUES);
export type FlowWaitKind = z.infer<typeof flowWaitKindSchema>;

/**
 * Why an enrolment left before the end. Never a free sentence in the database:
 * the CHECK allows any lower-snake word, and this list is the words the engine
 * uses, with the copy the screens show.
 */
export const FLOW_EXIT_REASON_VALUES = [
  'exit_node',
  'path_end',
  'suppressed',
  'no_address',
  'guard_refused',
  'insufficient_credits',
  'template_unavailable',
  'frequency_cap',
  'rested',
  'goal_window_closed',
  'stopped',
  'error',
] as const;
export const flowExitReasonSchema = z.enum(FLOW_EXIT_REASON_VALUES);
export type FlowExitReason = z.infer<typeof flowExitReasonSchema>;

export const FLOW_EXIT_REASON_LABEL: Readonly<Record<FlowExitReason, string>> = {
  exit_node: 'Reached an Exit step',
  path_end: 'Path ended',
  suppressed: 'On the suppression list',
  no_address: 'No address for this channel',
  guard_refused: 'Refused by the send guard',
  insufficient_credits: 'Not enough credits',
  template_unavailable: 'Template no longer approved',
  frequency_cap: 'Frequency cap stayed full',
  rested: 'Rested — message pressure stayed critical',
  goal_window_closed: 'Goal window closed',
  stopped: 'Flow stopped',
  error: 'Something went wrong',
};

/**
 * Kinds 0052's CHECK allows on `flow_trigger_events`. `segment_entered` is
 * allowed and no longer written (slice 71 review): a segment-entry trigger reads
 * `segment_membership_events` directly, and the copy the refresher used to
 * write had no reader. Kept so the CHECK and this list stay one list.
 */
export const FLOW_TRIGGER_EVENT_KIND_VALUES = ['whatsapp_reply', 'link_click', 'outcome', 'reach_lead', 'segment_entered'] as const;
export const flowTriggerEventKindSchema = z.enum(FLOW_TRIGGER_EVENT_KIND_VALUES);
export type FlowTriggerEventKind = z.infer<typeof flowTriggerEventKindSchema>;

export const FLOW_NODE_TYPE_VALUES = ['trigger', 'message', 'wait', 'condition', 'split', 'update', 'goal', 'exit', 'comment'] as const;
export const flowNodeTypeSchema = z.enum(FLOW_NODE_TYPE_VALUES);
export type FlowNodeType = z.infer<typeof flowNodeTypeSchema>;

export const FLOW_NODE_TYPE_LABEL: Readonly<Record<FlowNodeType, string>> = {
  trigger: 'Start',
  message: 'Message',
  wait: 'Wait',
  condition: 'Condition',
  split: 'Random split',
  update: 'Profile update',
  goal: 'Goal',
  exit: 'Exit',
  comment: 'Comment',
};

/** Starting points for `/flows/new` and for a Sense `flow_draft` action. */
export const FLOW_TEMPLATE_ID_VALUES = ['welcome_leads', 'birthday', 'winback', 'blank'] as const;
export const flowTemplateIdSchema = z.enum(FLOW_TEMPLATE_ID_VALUES);
export type FlowTemplateId = z.infer<typeof flowTemplateIdSchema>;

export const FLOW_TEMPLATES: Readonly<Record<FlowTemplateId, { label: string; description: string }>> = {
  welcome_leads: {
    label: 'Welcome series for new leads',
    description: 'Greets each Reach lead on WhatsApp, waits for a reply, and follows up once if there is none.',
  },
  birthday: {
    label: 'Birthday greeting',
    description: 'Runs every morning for people whose birthday — from any date column you choose — is today.',
  },
  winback: {
    label: 'Win back lapsed customers',
    description: 'Reaches people who have gone quiet, with a holdout so Sense can measure what it really changes.',
  },
  blank: {
    label: 'Blank',
    description: 'Just a Start step. Build it your way.',
  },
};

/** Shown by every Flow endpoint's 403 when the workspace lacks the `flow` feature. */
export const FLOW_NOT_ENTITLED_MESSAGE =
  'Flow is not part of this workspace’s plan. Ask your Wend contact to turn it on.';

/* -------------------------------------------------------------------------- */
/* Graph — ids, nodes, edges                                                   */
/* -------------------------------------------------------------------------- */

const SAFE_ID = /^[A-Za-z0-9_-]{1,64}$/;

export const flowNodeIdSchema = z.string().regex(SAFE_ID, 'A node id is 1–64 letters, digits, - or _');
export const flowPortIdSchema = z.string().regex(SAFE_ID, 'A port id is 1–64 letters, digits, - or _');
export const flowEdgeIdSchema = z.string().regex(SAFE_ID, 'An edge id is 1–64 letters, digits, - or _');

export const FLOW_MAX_NODES = 100;
export const FLOW_MAX_EDGES = 200;
export const FLOW_MAX_BUTTONS = 10;
export const FLOW_DEFAULT_NO_REPLY_HOURS = 24;

export const flowPositionSchema = z
  .object({ x: z.number().finite().min(-100_000).max(100_000), y: z.number().finite().min(-100_000).max(100_000) })
  .strict();

/** A suggested node: drawn dashed, accepted or rejected by a person, never launched. */
export const flowSuggestionMarkSchema = z.object({ reason: z.string().trim().min(1).max(500) }).strict();

const nodeBase = {
  id: flowNodeIdSchema,
  position: flowPositionSchema,
  suggested: flowSuggestionMarkSchema.optional(),
};

export const flowTriggerNodeSchema = z
  .object({ ...nodeBase, type: z.literal('trigger'), data: z.object({}).strict() })
  .strict();

/**
 * WhatsApp: the template (null while the person is still choosing — preflight
 * blocks launch), its quick-reply labels as saved, how long to wait for a tap,
 * and the variable bindings in the one shape every WhatsApp send uses.
 */
export const flowWhatsappMessageDataSchema = z
  .object({
    channel: z.literal('whatsapp'),
    templateId: uuidSchema.nullable(),
    buttons: z.array(z.string().min(1).max(60)).max(FLOW_MAX_BUTTONS),
    noReplyHours: z.number().int().min(1).max(168),
    vars: waBindingListSchema,
  })
  .strict();

/** Email: the template and the sender, because a campaign row needs both. */
export const flowEmailMessageDataSchema = z
  .object({
    channel: z.literal('email'),
    templateId: uuidSchema.nullable(),
    fromEmail: emailSchema.nullable(),
    fromName: z.string().trim().min(1).max(120).nullable(),
  })
  .strict();

export const flowMessageDataSchema = z.discriminatedUnion('channel', [flowWhatsappMessageDataSchema, flowEmailMessageDataSchema]);
export type FlowMessageData = z.infer<typeof flowMessageDataSchema>;

export const flowMessageNodeSchema = z.object({ ...nodeBase, type: z.literal('message'), data: flowMessageDataSchema }).strict();

export const FLOW_WAIT_UNIT_VALUES = ['minutes', 'hours', 'days'] as const;
export const flowWaitUnitSchema = z.enum(FLOW_WAIT_UNIT_VALUES);
export type FlowWaitUnit = z.infer<typeof flowWaitUnitSchema>;

/** `HH:mm`, 24-hour, IST. */
export const flowClockTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Write the time as HH:mm, 24-hour');

export const flowWaitDataSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('duration'), amount: z.number().int().min(1).max(10_000), unit: flowWaitUnitSchema }).strict(),
  z.object({ mode: z.literal('until'), at: isoDateTimeSchema }).strict(),
  /** The next `time` (IST) — on `weekday` (0 = Sunday) when given, else the next day it comes round. */
  z.object({ mode: z.literal('next'), weekday: z.number().int().min(0).max(6).nullable(), time: flowClockTimeSchema }).strict(),
]);
export type FlowWaitData = z.infer<typeof flowWaitDataSchema>;

export const flowWaitNodeSchema = z.object({ ...nodeBase, type: z.literal('wait'), data: flowWaitDataSchema }).strict();

/**
 * Journey events a condition can ask about, measured since THIS enrolment
 * entered — "replied since entry", not "has ever replied".
 */
export const FLOW_JOURNEY_FIELD_VALUES = ['replied_since_entry', 'clicked_since_entry', 'opened_since_entry', 'converted_since_entry'] as const;
export const flowJourneyFieldSchema = z.enum(FLOW_JOURNEY_FIELD_VALUES);
export type FlowJourneyField = z.infer<typeof flowJourneyFieldSchema>;

export const FLOW_JOURNEY_FIELD_LABEL: Readonly<Record<FlowJourneyField, string>> = {
  replied_since_entry: 'Replied since entering',
  clicked_since_entry: 'Clicked a link since entering',
  opened_since_entry: 'Opened an email since entering',
  converted_since_entry: 'Converted since entering',
};

export const flowJourneyRuleSchema = z
  .object({ kind: z.literal('journey'), id: ruleIdSchema, field: flowJourneyFieldSchema, op: z.enum(['is_true', 'is_false']) })
  .strict();
export type FlowJourneyRule = z.infer<typeof flowJourneyRuleSchema>;

/** A condition's rows: catalogue rules (slice 67's grammar) and journey events, one join. */
export const flowConditionDataSchema = z
  .object({
    join: ruleJoinSchema,
    rules: z.array(z.union([flowJourneyRuleSchema, ruleLeafSchema])).min(1, 'A condition needs at least one rule').max(20),
  })
  .strict();
export type FlowConditionData = z.infer<typeof flowConditionDataSchema>;

export const flowConditionNodeSchema = z.object({ ...nodeBase, type: z.literal('condition'), data: flowConditionDataSchema }).strict();

export const FLOW_HOLDOUT_PORT = 'holdout';

/**
 * Rows are the treated paths; `holdout` reserves people who get nothing, for
 * lift. Rows plus holdout must add to 100 — a PREFLIGHT rule (a person types
 * 45, then 45, then 10), so not refused here.
 */
export const flowSplitDataSchema = z
  .object({
    rows: z
      .array(
        z
          .object({
            id: flowPortIdSchema.refine((id) => id !== FLOW_HOLDOUT_PORT, 'holdout is reserved for the holdout port'),
            label: z.string().trim().min(1).max(40),
            pct: z.number().int().min(0).max(100),
          })
          .strict(),
      )
      .min(1)
      .max(5)
      .refine((rows) => new Set(rows.map((row) => row.id)).size === rows.length, 'Each path needs its own id'),
    holdout: z.object({ pct: z.number().int().min(1).max(50) }).strict().nullable(),
  })
  .strict();
export type FlowSplitData = z.infer<typeof flowSplitDataSchema>;

export const flowSplitNodeSchema = z.object({ ...nodeBase, type: z.literal('split'), data: flowSplitDataSchema }).strict();

/**
 * Profile updates write to the person's representative contact row, in the
 * tenant transaction, logged on the step. `set_field.key` is a COLUMN name (the
 * `extras` key, no prefix); a tag lives in the `tags` column as a list.
 */
export const flowUpdateDataSchema = z.discriminatedUnion('action', [
  z
    .object({
      action: z.literal('set_field'),
      key: z.string().trim().min(1).max(200).refine((key) => !key.startsWith('extras.'), 'Name the column itself, without “extras.”'),
      value: z.string().max(500),
    })
    .strict(),
  z.object({ action: z.literal('add_tag'), value: z.string().trim().min(1).max(60) }).strict(),
  z.object({ action: z.literal('remove_tag'), value: z.string().trim().min(1).max(60) }).strict(),
]);
export type FlowUpdateData = z.infer<typeof flowUpdateDataSchema>;

export const FLOW_TAGS_COLUMN = 'tags';

/**
 * Slice 68: a Profile update APPENDS a contact row (an imported contact is
 * immutable — 0003) to this per-workspace system dataset, and latest-upload-wins
 * makes it the person's value. Like "Reach leads", it is not an upload: readers
 * that list or name uploads exclude this `source_filename`.
 */
export const FLOW_UPDATES_DATASET_SOURCE = 'flow:updates';
export const FLOW_UPDATES_DATASET_NAME = 'Flow profile updates';

export const flowUpdateNodeSchema = z.object({ ...nodeBase, type: z.literal('update'), data: flowUpdateDataSchema }).strict();

/** A Matched outcome of this type (or any) within `withinDays` of entering ends the enrolment `converted`. */
export const flowGoalDataSchema = z
  .object({ outcomeTypeId: z.union([uuidSchema, z.literal('any')]), withinDays: z.number().int().min(1).max(365) })
  .strict();
export const flowGoalNodeSchema = z.object({ ...nodeBase, type: z.literal('goal'), data: flowGoalDataSchema }).strict();

export const flowExitDataSchema = z
  .object({ cooloffDays: z.number().int().min(0).max(365), note: z.string().trim().max(200).nullable() })
  .strict();
export const flowExitNodeSchema = z.object({ ...nodeBase, type: z.literal('exit'), data: flowExitDataSchema }).strict();

export const flowCommentNodeSchema = z
  .object({ ...nodeBase, type: z.literal('comment'), data: z.object({ text: z.string().max(1000) }).strict() })
  .strict();

export const flowNodeSchema = z.discriminatedUnion('type', [
  flowTriggerNodeSchema,
  flowMessageNodeSchema,
  flowWaitNodeSchema,
  flowConditionNodeSchema,
  flowSplitNodeSchema,
  flowUpdateNodeSchema,
  flowGoalNodeSchema,
  flowExitNodeSchema,
  flowCommentNodeSchema,
]);
export type FlowNode = z.infer<typeof flowNodeSchema>;
export type FlowNodeOf<T extends FlowNodeType> = Extract<FlowNode, { type: T }>;

export const flowEdgeSchema = z
  .object({ id: flowEdgeIdSchema, from: flowNodeIdSchema, port: flowPortIdSchema, to: flowNodeIdSchema })
  .strict();
export type FlowEdge = z.infer<typeof flowEdgeSchema>;

/**
 * The graph as stored on a version. Shape only; {@link validateGraph} is the
 * structure, and the API applies it on save.
 */
export const flowGraphSchema = z
  .object({
    nodes: z.array(flowNodeSchema).min(1).max(FLOW_MAX_NODES),
    edges: z.array(flowEdgeSchema).max(FLOW_MAX_EDGES),
  })
  .strict();
export type FlowGraph = z.infer<typeof flowGraphSchema>;

/* -------------------------------------------------------------------------- */
/* Ports                                                                       */
/* -------------------------------------------------------------------------- */

export const FLOW_PORT_KIND_VALUES = ['out', 'reply', 'noreply', 'yes', 'no', 'path', 'holdout'] as const;
export type FlowPortKind = (typeof FLOW_PORT_KIND_VALUES)[number];

export interface FlowPort {
  id: string;
  label: string;
  kind: FlowPortKind;
}

/** The quick-reply labels of a WhatsApp template, when the caller has the template to hand. */
export interface FlowPortTemplate {
  buttons: readonly string[];
}

export function whatsappButtonPort(index: number): string {
  return `btn${index}`;
}

/**
 * The ports a node offers, in display order. For a WhatsApp message the
 * buttons come from `template` when given (the approved template, freshest) and
 * otherwise from the node's saved snapshot.
 */
export function portsOf(node: FlowNode, template?: FlowPortTemplate | null): FlowPort[] {
  switch (node.type) {
    case 'trigger':
      return [{ id: 'out', label: 'Start', kind: 'out' }];
    case 'message': {
      if (node.data.channel === 'email') return [{ id: 'out', label: 'After the email', kind: 'out' }];
      const buttons = template?.buttons ?? node.data.buttons;
      if (buttons.length === 0) return [{ id: 'out', label: 'After the message', kind: 'out' }];
      return [
        ...buttons.map((label, index) => ({ id: whatsappButtonPort(index), label, kind: 'reply' as const })),
        { id: 'noreply', label: `No reply in ${node.data.noReplyHours}h`, kind: 'noreply' as const },
      ];
    }
    case 'wait':
      return [{ id: 'out', label: 'After the wait', kind: 'out' }];
    case 'condition':
      return [
        { id: 'yes', label: 'Yes', kind: 'yes' },
        { id: 'no', label: 'No', kind: 'no' },
      ];
    case 'split':
      return [
        ...node.data.rows.map((row) => ({ id: row.id, label: `${row.label} · ${row.pct}%`, kind: 'path' as const })),
        ...(node.data.holdout ? [{ id: FLOW_HOLDOUT_PORT, label: `Holdout · ${node.data.holdout.pct}%`, kind: 'holdout' as const }] : []),
      ];
    case 'update':
      return [{ id: 'out', label: 'After the update', kind: 'out' }];
    case 'goal':
    case 'exit':
    case 'comment':
      return [];
  }
}

/* -------------------------------------------------------------------------- */
/* Structural validation — shared by the API and the editor                    */
/* -------------------------------------------------------------------------- */

export const FLOW_GRAPH_ISSUE_CODE_VALUES = [
  'no_trigger',
  'several_triggers',
  'duplicate_node_id',
  'duplicate_edge_id',
  'edge_unknown_node',
  'unknown_port',
  'port_reused',
  'edge_into_trigger',
  'comment_edge',
  'self_loop',
  'cycle',
  /** Slice 68, API only: a message names a template this workspace does not have, or one of another channel. */
  'unknown_template',
] as const;
export const flowGraphIssueCodeSchema = z.enum(FLOW_GRAPH_ISSUE_CODE_VALUES);
export type FlowGraphIssueCode = z.infer<typeof flowGraphIssueCodeSchema>;

export const flowGraphIssueSchema = z
  .object({
    code: flowGraphIssueCodeSchema,
    message: z.string().min(1),
    nodeId: z.string().nullable(),
    edgeId: z.string().nullable(),
  })
  .strict();
export type FlowGraphIssue = z.infer<typeof flowGraphIssueSchema>;

/**
 * The structure, and nothing else: one trigger, a DAG, edges between nodes
 * that exist from ports they have, one edge per port, nothing into the
 * trigger, nothing touching a comment. Returns every issue, not the first, so
 * the editor can mark them all. `templates` (by template id) lets the API check
 * ports against the approved template rather than the saved snapshot.
 */
export function validateGraph(
  graph: Pick<FlowGraph, 'nodes' | 'edges'>,
  templates?: ReadonlyMap<string, FlowPortTemplate>,
): FlowGraphIssue[] {
  const issues: FlowGraphIssue[] = [];
  const push = (code: FlowGraphIssueCode, message: string, nodeId: string | null = null, edgeId: string | null = null) =>
    issues.push({ code, message, nodeId, edgeId });

  const byId = new Map<string, FlowNode>();
  for (const node of graph.nodes) {
    if (byId.has(node.id)) push('duplicate_node_id', `Two steps share the id “${node.id}”.`, node.id);
    else byId.set(node.id, node);
  }

  const triggers = graph.nodes.filter((node) => node.type === 'trigger');
  if (triggers.length === 0) push('no_trigger', 'A flow needs a Start step.');
  if (triggers.length > 1) {
    for (const extra of triggers.slice(1)) push('several_triggers', 'A flow has exactly one Start step.', extra.id);
  }

  const edgeIds = new Set<string>();
  const usedPorts = new Map<string, string>();
  const live: FlowEdge[] = [];
  for (const edge of graph.edges) {
    if (edgeIds.has(edge.id)) {
      push('duplicate_edge_id', `Two connections share the id “${edge.id}”.`, null, edge.id);
      continue;
    }
    edgeIds.add(edge.id);
    const from = byId.get(edge.from);
    const to = byId.get(edge.to);
    if (!from || !to) {
      push('edge_unknown_node', 'A connection points at a step that no longer exists.', from ? null : edge.from, edge.id);
      continue;
    }
    if (from.type === 'comment' || to.type === 'comment') {
      push('comment_edge', 'A comment is a note on the canvas and cannot be connected.', from.type === 'comment' ? from.id : to.id, edge.id);
      continue;
    }
    if (to.type === 'trigger') {
      push('edge_into_trigger', 'Nothing can lead back into Start.', to.id, edge.id);
      continue;
    }
    if (edge.from === edge.to) {
      push('self_loop', 'A step cannot lead to itself.', from.id, edge.id);
      continue;
    }
    const template = from.type === 'message' && from.data.templateId ? templates?.get(from.data.templateId) : undefined;
    const ports = portsOf(from, template);
    if (!ports.some((port) => port.id === edge.port)) {
      push('unknown_port', `“${FLOW_NODE_TYPE_LABEL[from.type]}” has no “${edge.port}” path — its template or its rows changed.`, from.id, edge.id);
      continue;
    }
    const portKey = `${edge.from}\u0000${edge.port}`;
    if (usedPorts.has(portKey)) {
      push('port_reused', 'Each path can lead to one step only.', from.id, edge.id);
      continue;
    }
    usedPorts.set(portKey, edge.id);
    live.push(edge);
  }

  // Kahn: whatever cannot be ordered sits on a cycle.
  const indegree = new Map<string, number>();
  for (const node of byId.values()) indegree.set(node.id, 0);
  for (const edge of live) indegree.set(edge.to, (indegree.get(edge.to) ?? 0) + 1);
  const queue = [...indegree].filter(([, count]) => count === 0).map(([id]) => id);
  let ordered = 0;
  while (queue.length > 0) {
    const id = queue.shift() as string;
    ordered += 1;
    for (const edge of live) {
      if (edge.from !== id) continue;
      const left = (indegree.get(edge.to) ?? 0) - 1;
      indegree.set(edge.to, left);
      if (left === 0) queue.push(edge.to);
    }
  }
  if (ordered < byId.size) {
    const looped = [...indegree].find(([, count]) => count > 0)?.[0] ?? null;
    push('cycle', 'This flow loops back on itself. Flows run forward only — use a Wait and a Condition instead of a loop.', looped);
  }

  return issues;
}

/** Node ids reachable from the trigger along the given edges. */
export function reachableFrom(graph: Pick<FlowGraph, 'edges'>, startId: string): Set<string> {
  const seen = new Set([startId]);
  const stack = [startId];
  while (stack.length > 0) {
    const id = stack.pop() as string;
    for (const edge of graph.edges) {
      if (edge.from === id && !seen.has(edge.to)) {
        seen.add(edge.to);
        stack.push(edge.to);
      }
    }
  }
  return seen;
}

/** The edge leaving `nodeId` by `port`, if any. */
export function edgeFrom(graph: Pick<FlowGraph, 'edges'>, nodeId: string, port: string): FlowEdge | undefined {
  return graph.edges.find((edge) => edge.from === nodeId && edge.port === port);
}

/* -------------------------------------------------------------------------- */
/* Triggers and settings                                                       */
/* -------------------------------------------------------------------------- */

export const FLOW_TIMEZONE = 'Asia/Kolkata';

export const FLOW_SCHEDULE_EVERY_VALUES = ['day', 'week', 'month'] as const;
export const flowScheduleEverySchema = z.enum(FLOW_SCHEDULE_EVERY_VALUES);
export type FlowScheduleEvery = z.infer<typeof flowScheduleEverySchema>;

/**
 * Once at an instant, or recurring in IST. A recurring schedule's occurrence
 * ordinal is the enrolment `cycle`, and `flow_runs` makes each occurrence
 * exactly-once. `dayOfMonth` stops at 28 so every month has the day.
 */
export const flowScheduleSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('once'), at: isoDateTimeSchema }).strict(),
  z
    .object({
      mode: z.literal('recurring'),
      every: flowScheduleEverySchema,
      time: flowClockTimeSchema,
      weekdays: z.array(z.number().int().min(0).max(6)).max(7).nullable(),
      dayOfMonth: z.number().int().min(1).max(28).nullable(),
      startsOn: ruleDateSchema,
      endsOn: ruleDateSchema.nullable(),
    })
    .strict(),
]);
export type FlowSchedule = z.infer<typeof flowScheduleSchema>;

function scheduleIssues(schedule: FlowSchedule): string | null {
  if (schedule.mode === 'once') return null;
  if (schedule.every === 'week') {
    if (!schedule.weekdays || schedule.weekdays.length === 0) return 'Pick at least one day of the week';
    if (new Set(schedule.weekdays).size !== schedule.weekdays.length) return 'Each weekday only once';
  } else if (schedule.weekdays !== null) {
    return 'Weekdays only apply to a weekly schedule';
  }
  if ((schedule.every === 'month') !== (schedule.dayOfMonth !== null)) {
    return schedule.every === 'month' ? 'Pick the day of the month' : 'A day of the month only applies to a monthly schedule';
  }
  if (schedule.endsOn !== null && schedule.endsOn < schedule.startsOn) return 'The schedule ends before it starts';
  return null;
}

export const flowEventTriggerSchema = z.discriminatedUnion('kind', [
  /** A WhatsApp reply; `payload` narrows to one quick-reply button's payload. */
  z.object({ kind: z.literal('whatsapp_reply'), payload: z.string().trim().min(1).max(256).nullable() }).strict(),
  z.object({ kind: z.literal('link_click') }).strict(),
  /** A Matched outcome; `outcomeTypeId` narrows to one declared type. */
  z.object({ kind: z.literal('outcome'), outcomeTypeId: uuidSchema.nullable() }).strict(),
  /** A Reach lead landing; `adId` narrows to one ad. */
  z.object({ kind: z.literal('reach_lead'), adId: z.string().trim().min(1).max(64).nullable() }).strict(),
]);
export type FlowEventTrigger = z.infer<typeof flowEventTriggerSchema>;

export const FLOW_TRIGGER_TYPE_VALUES = ['segment_entry', 'schedule', 'event', 'manual'] as const;
export type FlowTriggerType = (typeof FLOW_TRIGGER_TYPE_VALUES)[number];

/**
 * The schedule's cross-field rules (weekly needs weekdays, monthly a day…) sit
 * on the union rather than on its `schedule` arm, because a discriminated union
 * can only hold plain objects.
 */
export const flowTriggerSchema = z
  .discriminatedUnion('type', [
    /**
     * Enrols each person who ENTERS the rule segment after launch.
     * `includeExisting` also enrols the current members once, at launch.
     */
    z.object({ type: z.literal('segment_entry'), segmentId: uuidSchema, includeExisting: z.boolean() }).strict(),
    z.object({ type: z.literal('schedule'), schedule: flowScheduleSchema }).strict(),
    z.object({ type: z.literal('event'), event: flowEventTriggerSchema }).strict(),
    z.object({ type: z.literal('manual') }).strict(),
  ])
  .superRefine((trigger, ctx) => {
    if (trigger.type !== 'schedule') return;
    const issue = scheduleIssues(trigger.schedule);
    if (issue) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['schedule'], message: issue });
  });
export type FlowTrigger = z.infer<typeof flowTriggerSchema>;

export const FLOW_REENTRY_VALUES = ['never', 'after_days', 'always'] as const;
export const flowReentrySchema = z.enum(FLOW_REENTRY_VALUES);
export type FlowReentry = z.infer<typeof flowReentrySchema>;

export const flowQuietHoursSchema = z
  .object({
    /** Hour (IST) quiet hours begin — nothing is sent from here… */
    start: z.number().int().min(0).max(23),
    /** …until this hour. start 21, end 9 is 21:00–09:00. */
    end: z.number().int().min(0).max(23),
    tz: z.literal(FLOW_TIMEZONE),
  })
  .strict();
export type FlowQuietHours = z.infer<typeof flowQuietHoursSchema>;

/** At most `max` messages — flow AND Connect — to one person in `perDays` days. */
export const flowFrequencyCapSchema = z
  .object({ max: z.number().int().min(1).max(20), perDays: z.number().int().min(1).max(30) })
  .strict();
export type FlowFrequencyCap = z.infer<typeof flowFrequencyCapSchema>;

export const flowSettingsSchema = z
  .object({
    trigger: flowTriggerSchema,
    /**
     * Who may enter. Required for a schedule (each occurrence enrols its
     * current members); an optional filter for event and manual triggers;
     * for segment entry it must be null or the trigger's own segment.
     */
    audienceSegmentId: uuidSchema.nullable(),
    reentry: flowReentrySchema,
    /** Set exactly when `reentry` is `after_days`. */
    reentryDays: z.number().int().min(1).max(365).nullable(),
    quietHours: flowQuietHoursSchema,
    frequencyCap: flowFrequencyCapSchema,
    /** Defer a person whose message pressure is Critical (one day, three times, then they leave rested). */
    restCritical: z.boolean(),
    /**
     * A flow-wide holdout taken at entry, for flows without a split. Null when
     * none. A split's `holdout` port is the other way to reserve one.
     */
    holdoutPct: z.number().int().min(1).max(50).nullable(),
  })
  .strict()
  .superRefine((settings, ctx) => {
    if ((settings.reentry === 'after_days') !== (settings.reentryDays !== null)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['reentryDays'],
        message: settings.reentry === 'after_days' ? 'Say after how many days someone may enter again' : 'Days only apply to “after some days”',
      });
    }
    if (settings.trigger.type === 'schedule' && settings.audienceSegmentId === null) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['audienceSegmentId'], message: 'A scheduled flow needs an audience segment' });
    }
    if (
      settings.trigger.type === 'segment_entry' &&
      settings.audienceSegmentId !== null &&
      settings.audienceSegmentId !== settings.trigger.segmentId
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['audienceSegmentId'],
        message: 'A segment-entry flow’s audience is its trigger segment',
      });
    }
  });
export type FlowSettings = z.infer<typeof flowSettingsSchema>;

export const DEFAULT_FLOW_SETTINGS: FlowSettings = {
  trigger: { type: 'manual' },
  audienceSegmentId: null,
  reentry: 'never',
  reentryDays: null,
  quietHours: { start: 21, end: 9, tz: FLOW_TIMEZONE },
  frequencyCap: { max: 2, perDays: 7 },
  restCritical: true,
  holdoutPct: null,
};

/** `flow_versions.cursor`. */
export const flowVersionCursorSchema = z.object({ lastEventId: uuidSchema.optional() }).strict();
export type FlowVersionCursor = z.infer<typeof flowVersionCursorSchema>;

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/**
 * One line for a list or a card: "When someone enters Lapsing buyers",
 * "Every Mon, Thu at 10:00 IST". Segment names are the caller's to supply.
 */
export function describeTrigger(trigger: FlowTrigger, segmentName?: string | null): string {
  switch (trigger.type) {
    case 'segment_entry':
      return `When someone enters ${segmentName ? `“${segmentName}”` : 'a segment'}`;
    case 'manual':
      return 'When you enrol people';
    case 'event':
      switch (trigger.event.kind) {
        case 'whatsapp_reply':
          return trigger.event.payload ? `When someone taps “${trigger.event.payload}” on WhatsApp` : 'When someone replies on WhatsApp';
        case 'link_click':
          return 'When someone clicks a link';
        case 'outcome':
          return 'When an outcome is recorded';
        case 'reach_lead':
          return 'When a Reach lead arrives';
      }
      break;
    case 'schedule': {
      const { schedule } = trigger;
      if (schedule.mode === 'once') {
        const when = new Date(schedule.at).toLocaleString('en-IN', {
          timeZone: FLOW_TIMEZONE,
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        });
        return `Once, ${when} IST`;
      }
      if (schedule.every === 'day') return `Every day at ${schedule.time} IST`;
      if (schedule.every === 'week') {
        const days = (schedule.weekdays ?? []).slice().sort((a, b) => a - b).map((d) => WEEKDAY_SHORT[d]).join(', ');
        return `Every ${days} at ${schedule.time} IST`;
      }
      return `Monthly on day ${schedule.dayOfMonth ?? 1} at ${schedule.time} IST`;
    }
  }
  return 'Manual';
}

/* -------------------------------------------------------------------------- */
/* API — list and detail                                                       */
/* -------------------------------------------------------------------------- */

export const flowParamsSchema = z.object({ flowId: uuidSchema }).strict();
export type FlowParams = z.infer<typeof flowParamsSchema>;

export const flowNameSchema = z.string().trim().min(1).max(120);
export const flowDescriptionSchema = z
  .string()
  .trim()
  .max(500)
  .transform((value) => (value === '' ? null : value))
  .nullable();

/**
 * Computed on read from enrolments. `completed` is people who walked the
 * journey to its end; `heldOut` is people a holdout reserved (they finish at
 * once, with nothing sent) — counted apart, because a 10% holdout inside
 * "Finished" claimed journeys nobody walked (slice 71 review).
 */
export const flowCountsSchema = z
  .object({
    active: z.number().int().min(0),
    completed: z.number().int().min(0),
    heldOut: z.number().int().min(0),
    converted: z.number().int().min(0),
  })
  .strict();
export type FlowCounts = z.infer<typeof flowCountsSchema>;

export const flowSummarySchema = z
  .object({
    id: uuidSchema,
    name: z.string(),
    description: z.string().nullable(),
    status: flowStatusSchema,
    /** The live version's trigger, else the draft's. */
    trigger: flowTriggerSchema,
    /** {@link describeTrigger}, with segment names resolved by the API. */
    triggerSummary: z.string().min(1),
    liveVersion: z.object({ id: uuidSchema, version: z.number().int().min(1), launchedAt: isoDateTimeSchema }).strict().nullable(),
    /** The draft's number when one exists ("Editing draft v3 · v2 is live"). */
    draftVersion: z.number().int().min(1).nullable(),
    counts: flowCountsSchema,
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
  })
  .strict();
export type FlowSummary = z.infer<typeof flowSummarySchema>;

export const flowListResponseSchema = z.object({ flows: z.array(flowSummarySchema) }).strict();
export type FlowListResponse = z.infer<typeof flowListResponseSchema>;

export const flowVersionSummarySchema = z
  .object({
    id: uuidSchema,
    version: z.number().int().min(1),
    status: flowVersionStatusSchema,
    createdAt: isoDateTimeSchema,
    launchedAt: isoDateTimeSchema.nullable(),
    retiredAt: isoDateTimeSchema.nullable(),
  })
  .strict();
export type FlowVersionSummary = z.infer<typeof flowVersionSummarySchema>;

export const flowVersionDetailSchema = z
  .object({
    id: uuidSchema,
    version: z.number().int().min(1),
    status: flowVersionStatusSchema,
    graph: flowGraphSchema,
    settings: flowSettingsSchema,
    updatedAt: isoDateTimeSchema,
    launchedAt: isoDateTimeSchema.nullable(),
  })
  .strict();
export type FlowVersionDetail = z.infer<typeof flowVersionDetailSchema>;

export const flowDetailSchema = z
  .object({
    id: uuidSchema,
    name: z.string(),
    description: z.string().nullable(),
    status: flowStatusSchema,
    versions: z.array(flowVersionSummarySchema),
    /** The version being edited. Null when the live version has no draft beside it. */
    draft: flowVersionDetailSchema.nullable(),
    /** The version people are walking (live or paused). */
    live: flowVersionDetailSchema.nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
  })
  .strict();
export type FlowDetail = z.infer<typeof flowDetailSchema>;

export const flowResponseSchema = z.object({ flow: flowDetailSchema }).strict();
export type FlowResponse = z.infer<typeof flowResponseSchema>;

/* ---- create and save ------------------------------------------------------ */

/**
 * `POST /flows`. From a template (graph built by the API) or with a graph and
 * settings of the caller's own — both or neither. `audienceSegmentId` seeds a
 * template's audience (a Sense `flow_draft` passes the segment it found).
 * Always a draft: nothing is launched by creating.
 */
export const createFlowRequestSchema = z
  .object({
    name: flowNameSchema,
    description: flowDescriptionSchema.optional().default(null),
    template: flowTemplateIdSchema.default('blank'),
    audienceSegmentId: uuidSchema.nullable().optional().default(null),
    graph: flowGraphSchema.optional(),
    settings: flowSettingsSchema.optional(),
  })
  .strict()
  .refine((body) => (body.graph === undefined) === (body.settings === undefined), {
    message: 'Send a graph and its settings together, or neither',
    path: ['graph'],
  });
export type CreateFlowRequest = z.infer<typeof createFlowRequestSchema>;

/** `PUT /flows/:flowId/draft` — the editor's autosave. Creates the draft version when the flow is live. */
export const saveFlowDraftRequestSchema = z
  .object({
    graph: flowGraphSchema,
    settings: flowSettingsSchema,
    name: flowNameSchema.optional(),
    description: flowDescriptionSchema.optional(),
  })
  .strict();
export type SaveFlowDraftRequest = z.infer<typeof saveFlowDraftRequestSchema>;

/** The 422 body when a save breaks the structure ({@link validateGraph}) or names a template that does not exist. */
export const flowGraphRefusalSchema = z
  .object({ message: z.string().min(1), issues: z.array(flowGraphIssueSchema).min(1) })
  .strict();
export type FlowGraphRefusal = z.infer<typeof flowGraphRefusalSchema>;

/* ---- preflight and launch ------------------------------------------------- */

export const FLOW_CHECK_TONE_VALUES = ['good', 'warn', 'bad'] as const;
export const flowCheckToneSchema = z.enum(FLOW_CHECK_TONE_VALUES);
export type FlowCheckTone = z.infer<typeof flowCheckToneSchema>;

/**
 * One preflight finding, as the concept's `preflight()` writes them: start
 * connected, a message reachable, splits add to 100, approved templates only,
 * consent per channel, quiet hours, frequency cap, holdout, empty paths, orphans,
 * pending suggestions. `bad` blocks launch.
 */
export const flowPreflightCheckSchema = z
  .object({
    id: z.string().min(1).max(80),
    tone: flowCheckToneSchema,
    title: z.string().min(1),
    body: z.string(),
    /** Where to go to fix it. */
    fix: z
      .object({ label: z.string().min(1), nodeId: flowNodeIdSchema.nullable(), port: flowPortIdSchema.nullable() })
      .strict()
      .nullable(),
  })
  .strict();
export type FlowPreflightCheck = z.infer<typeof flowPreflightCheckSchema>;

export const flowPreflightResponseSchema = z
  .object({
    checks: z.array(flowPreflightCheckSchema),
    /** True exactly when a check is `bad`. */
    blocking: z.boolean(),
    /** Who would enter at launch. Null where it cannot be known yet (an event trigger with no audience). */
    audience: z
      .object({
        people: z.number().int().min(0).nullable(),
        emailReachable: z.number().int().min(0).nullable(),
        phoneReachable: z.number().int().min(0).nullable(),
      })
      .strict(),
    /** Projected messages and cost over the launch audience, from credit prices. Null when the audience is unknown. */
    projectedCost: z
      .object({
        whatsappMessages: z.number().int().min(0),
        emailMessages: z.number().int().min(0),
        /** Integer paise. Null when no price is set for the channel. */
        whatsappMinor: z.number().int().min(0).nullable(),
        emailMinor: z.number().int().min(0).nullable(),
        totalMinor: z.number().int().min(0).nullable(),
      })
      .strict()
      .nullable(),
    generatedAt: isoDateTimeSchema,
  })
  .strict()
  .refine((body) => body.blocking === body.checks.some((check) => check.tone === 'bad'), {
    message: 'blocking must be true exactly when a check is bad',
    path: ['blocking'],
  });
export type FlowPreflightResponse = z.infer<typeof flowPreflightResponseSchema>;

/** The 422 body of `POST /flows/:flowId/launch` when a check blocks. */
export const flowLaunchRefusalSchema = z
  .object({ message: z.string().min(1), checks: z.array(flowPreflightCheckSchema).min(1) })
  .strict();
export type FlowLaunchRefusal = z.infer<typeof flowLaunchRefusalSchema>;

/** `POST /flows/:flowId/archive`. `stopAll` also exits everyone still walking it (`stopped`). */
export const archiveFlowRequestSchema = z.object({ stopAll: z.boolean().default(false) }).strict();
export type ArchiveFlowRequest = z.infer<typeof archiveFlowRequestSchema>;

/* ---- enrol (manual trigger) ----------------------------------------------- */

export const FLOW_ENROL_MAX_PEOPLE = 500;

/** `POST /flows/:flowId/enrol` — a segment's current members, or listed people. */
export const flowEnrolRequestSchema = z.union([
  z.object({ segmentId: uuidSchema }).strict(),
  z
    .object({
      personIds: z
        .array(uuidSchema)
        .min(1)
        .max(FLOW_ENROL_MAX_PEOPLE)
        .refine((ids) => new Set(ids).size === ids.length, 'Each person only once'),
    })
    .strict(),
]);
export type FlowEnrolRequest = z.infer<typeof flowEnrolRequestSchema>;

export const flowEnrolResponseSchema = z
  .object({
    enrolled: z.number().int().min(0),
    /** Already in this version (and cycle), or held back by the re-entry rule. */
    alreadyEnrolled: z.number().int().min(0),
    /** Not enrolled, by reason (not in the audience, cooling off after an Exit…). */
    skipped: z.array(z.object({ reason: z.string().min(1), count: z.number().int().min(1) }).strict()),
  })
  .strict();
export type FlowEnrolResponse = z.infer<typeof flowEnrolResponseSchema>;

/* ---- test (dry walk) ------------------------------------------------------ */

/** `POST /flows/:flowId/test` — walk the draft (or live) graph for one real person. Sends nothing. */
export const flowTestRequestSchema = z
  .object({ personId: uuidSchema, version: z.enum(['draft', 'live']).default('draft') })
  .strict();
export type FlowTestRequest = z.infer<typeof flowTestRequestSchema>;

export const flowTestPreviewSchema = z.discriminatedUnion('channel', [
  z
    .object({
      channel: z.literal('whatsapp'),
      header: z.string().nullable(),
      body: z.string(),
      footer: z.string().nullable(),
      buttons: z.array(z.string()),
    })
    .strict(),
  z.object({ channel: z.literal('email'), subject: z.string(), text: z.string() }).strict(),
]);
export type FlowTestPreview = z.infer<typeof flowTestPreviewSchema>;

export const flowTestStepSchema = z
  .object({
    /** Null for the closing line ("Path ends here", "Measured, not messaged"). */
    nodeId: flowNodeIdSchema.nullable(),
    title: z.string().min(1),
    lines: z.array(z.string()),
    /** When it would happen, projected from now in IST — after quiet hours and waits. */
    at: isoDateTimeSchema.nullable(),
    /** The port taken, if the step leaves by one. */
    port: flowPortIdSchema.nullable(),
    preview: flowTestPreviewSchema.nullable(),
  })
  .strict();
export type FlowTestStep = z.infer<typeof flowTestStepSchema>;

export const flowTestResponseSchema = z
  .object({
    person: maskedPersonSchema,
    steps: z.array(flowTestStepSchema),
    /** Always true. On the wire so no reader has to wonder. */
    sendsNothing: z.literal(true),
  })
  .strict();
export type FlowTestResponse = z.infer<typeof flowTestResponseSchema>;

/**
 * `GET /flows/:flowId/test-people` — added by the slice-69 builder. Who the
 * Test panel offers to walk the graph as: real people from the flow's audience
 * (or trigger segment), masked exactly as {@link maskedPersonSchema} masks
 * everywhere else. The Test request needs a person id, and no other endpoint
 * hands the browser a person id for an arbitrary segment — contacts carry rows,
 * not people. Until the API serves it, the editor falls back to enrolments,
 * rule-segment membership events and Reach leads, and says so when none exist.
 */
export const FLOW_TEST_PEOPLE_LIMIT = 20;
export const flowTestPeopleResponseSchema = z
  .object({
    people: z.array(maskedPersonSchema).max(FLOW_TEST_PEOPLE_LIMIT),
    /** Where they came from, as a sentence: "From “New leads · Pune”". */
    source: z.string().min(1),
  })
  .strict();
export type FlowTestPeopleResponse = z.infer<typeof flowTestPeopleResponseSchema>;

/* ---- stats ---------------------------------------------------------------- */

export const flowStatsQuerySchema = z.object({ version: z.coerce.number().int().min(1).optional() }).strict();
export type FlowStatsQuery = z.infer<typeof flowStatsQuerySchema>;

/**
 * Per node, computed on read. Message counts are distinct messages; `delivered`
 * / `read` / `replied` are null where the channel cannot measure them (email has
 * no read; WhatsApp has no open), never 0.
 */
export const flowNodeStatsSchema = z
  .object({
    nodeId: flowNodeIdSchema,
    entered: z.number().int().min(0),
    completed: z.number().int().min(0),
    waitingNow: z.number().int().min(0),
    /** People who left by each port. */
    ports: z.record(flowPortIdSchema, z.number().int().min(0)),
    messages: z
      .object({
        sent: z.number().int().min(0),
        delivered: z.number().int().min(0).nullable(),
        read: z.number().int().min(0).nullable(),
        replied: z.number().int().min(0).nullable(),
        skipped: z.number().int().min(0),
      })
      .strict()
      .nullable(),
    converted: z.number().int().min(0),
  })
  .strict();
export type FlowNodeStats = z.infer<typeof flowNodeStatsSchema>;

/**
 * Holdout against treated, under the same sample-floor rule as campaign
 * holdouts: below the floor the rates and the lift are null and `readable` is
 * false with a reason — never a confident zero.
 */
export const flowHoldoutStatsSchema = z
  .object({
    holdoutPeople: z.number().int().min(0),
    holdoutConverted: z.number().int().min(0),
    treatedPeople: z.number().int().min(0),
    treatedConverted: z.number().int().min(0),
    holdoutRate: z.number().min(0).max(1).nullable(),
    treatedRate: z.number().min(0).max(1).nullable(),
    /** treatedRate / holdoutRate − 1. */
    lift: z.number().nullable(),
    readable: z.boolean(),
    reason: z.string().nullable(),
  })
  .strict();
export type FlowHoldoutStats = z.infer<typeof flowHoldoutStatsSchema>;

export const flowStatsResponseSchema = z
  .object({
    flowId: uuidSchema,
    versionId: uuidSchema,
    version: z.number().int().min(1),
    generatedAt: isoDateTimeSchema,
    totals: z
      .object({
        entered: z.number().int().min(0),
        active: z.number().int().min(0),
        waiting: z.number().int().min(0),
        /** Walked to the end — people held back by a holdout are `heldOut`, not here. */
        completed: z.number().int().min(0),
        heldOut: z.number().int().min(0),
        converted: z.number().int().min(0),
        exited: z.number().int().min(0),
        failed: z.number().int().min(0),
      })
      .strict(),
    nodes: z.array(flowNodeStatsSchema),
    /** People who crossed each edge — the run-mode canvas draws thickness from it. */
    edges: z.array(z.object({ edgeId: flowEdgeIdSchema, volume: z.number().int().min(0) }).strict()),
    /** Null when the version reserves no holdout. */
    holdout: flowHoldoutStatsSchema.nullable(),
  })
  .strict();
export type FlowStatsResponse = z.infer<typeof flowStatsResponseSchema>;

/* ---- enrolments ----------------------------------------------------------- */

export const flowEnrollmentsQuerySchema = paginationQuerySchema
  .extend({
    node: flowNodeIdSchema.optional(),
    status: flowEnrollmentStatusSchema.optional(),
    version: z.coerce.number().int().min(1).optional(),
  })
  .strict();
export type FlowEnrollmentsQuery = z.infer<typeof flowEnrollmentsQuerySchema>;

export const flowEnrollmentSummarySchema = z
  .object({
    id: uuidSchema,
    person: maskedPersonSchema,
    versionId: uuidSchema,
    version: z.number().int().min(1),
    cycle: z.number().int().min(1),
    status: flowEnrollmentStatusSchema,
    currentNode: flowNodeIdSchema.nullable(),
    waitKind: flowWaitKindSchema.nullable(),
    enteredAt: isoDateTimeSchema,
    nextRunAt: isoDateTimeSchema.nullable(),
    finishedAt: isoDateTimeSchema.nullable(),
    exitReason: z.string().nullable(),
  })
  .strict();
export type FlowEnrollmentSummary = z.infer<typeof flowEnrollmentSummarySchema>;

export const flowEnrollmentListResponseSchema = paginated(flowEnrollmentSummarySchema);
export type FlowEnrollmentListResponse = z.infer<typeof flowEnrollmentListResponseSchema>;

export const flowEnrollmentParamsSchema = z.object({ flowId: uuidSchema, enrollmentId: uuidSchema }).strict();
export type FlowEnrollmentParams = z.infer<typeof flowEnrollmentParamsSchema>;

export const flowStepSchema = z
  .object({
    id: uuidSchema,
    nodeId: z.string(),
    kind: flowNodeTypeSchema,
    port: z.string().nullable(),
    outcome: z.string(),
    detail: z.record(z.string(), z.unknown()),
    messageId: uuidSchema.nullable(),
    at: isoDateTimeSchema,
  })
  .strict();
export type FlowStep = z.infer<typeof flowStepSchema>;

export const flowEnrollmentDetailResponseSchema = z
  .object({ enrollment: flowEnrollmentSummarySchema, steps: z.array(flowStepSchema) })
  .strict();
export type FlowEnrollmentDetailResponse = z.infer<typeof flowEnrollmentDetailResponseSchema>;

/* ---- suggest -------------------------------------------------------------- */

/** `POST /flows/:flowId/suggest` — what could follow this port. Deterministic; no AI call. */
export const flowSuggestRequestSchema = z.object({ afterNodeId: flowNodeIdSchema, port: flowPortIdSchema }).strict();
export type FlowSuggestRequest = z.infer<typeof flowSuggestRequestSchema>;

export const flowSuggestResponseSchema = z
  .object({
    /** Null when no rule has anything to offer here — and `reason` says so. */
    suggestion: z
      .object({
        /** Carries `suggested: { reason }`: the editor adds it dashed, for a person to accept. */
        node: flowNodeSchema.refine((node) => node.suggested !== undefined, 'A suggestion is marked suggested'),
        edge: z.object({ from: flowNodeIdSchema, port: flowPortIdSchema, to: flowNodeIdSchema }).strict(),
      })
      .strict()
      .nullable(),
    reason: z.string().min(1),
  })
  .strict();
export type FlowSuggestResponse = z.infer<typeof flowSuggestResponseSchema>;
